import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faSpinner, faGlobe, faLink, faRotateRight, faDesktop } from "@fortawesome/free-solid-svg-icons";
import { faFacebook, faInstagram, faWhatsapp, faTelegram, faSlack } from "@fortawesome/free-brands-svg-icons";
import SlackLogo from "./SlackLogo";
import dayjs from 'dayjs';
import ChatHistoryOverview from './chatHistoryOverview.js';
import ConfirmDownloadModal from './confirmDownloadModal.js';
import './analytics.css';
import './ChatHistories.css';
import { useTranslation } from 'react-i18next';
import '../translations.js';
import { canSeeWhatsApp } from '../utils/featureFlags';

const getUserDateFormat = () => {
  const stored = localStorage.getItem("date_format");
  if (stored === "YYYY-MM-DD" || stored === "DD-MM-YYYY" || stored === "MM-DD-YYYY") {
    return stored;
  }
  return "YYYY-MM-DD";
};

const formatSessionDate = (isoDate) => {
  if (!isoDate) return "";
  const d = dayjs(isoDate);
  if (!d.isValid()) return isoDate;
  const pattern = getUserDateFormat();
  return d.format(pattern);
};

const PLATFORM_FILTERS_BASE = [
  { key: 'website', label: 'Website', labelKey: 'chat_hist_channel_website', icon: faGlobe, color: '#5616ea' },
  { key: 'chat_link', label: 'Chat Link', labelKey: 'chat_hist_channel_chat_link', icon: faLink, color: '#5616ea' },
  { key: 'facebook', label: 'Facebook', icon: faFacebook, color: '#1877f2' },
  { key: 'instagram', label: 'Instagram', icon: faInstagram, color: '#E4405F' },
  { key: 'whatsapp', label: 'WhatsApp', icon: faWhatsapp, color: '#25D366' },
  { key: 'telegram', label: 'Telegram', icon: faTelegram, color: '#229ED9' },
  { key: 'slack', label: 'Slack', icon: faSlack, color: '#2EB67D' },
  // chats inside the portal (AI Studio, Test Agent window): the backend's 'portal' bucket (chat_history.py)
  { key: 'portal', label: 'Portal', labelKey: 'chat_hist_channel_portal', icon: faDesktop, color: '#5616ea' },
];

// Keep inbox filters aligned with the available messaging channels.
const getPlatformFilters = () =>
  canSeeWhatsApp() ? PLATFORM_FILTERS_BASE : PLATFORM_FILTERS_BASE.filter((f) => f.key !== 'whatsapp');

// How many session summaries one /api/chat_sessions page carries. The server
// caps at 200; scrolling the list asks for the next page.
const SESSIONS_PAGE_SIZE = 50;

// Same SVG-stroke draw-around animation we used for the old tabs — the line
// starts at the top-left corner and circles the chip clockwise. Renders inside
// each filter chip; only animates when the chip carries the `.active` class.
function PlatformFilterBorderDraw() {
  return (
    <svg className="platform-filter-border-svg" aria-hidden="true" focusable="false">
      <rect className="platform-filter-border-rect" pathLength="100" />
    </svg>
  );
}

// The Inbox container. Fetches paged per-session SUMMARIES from
// /api/chat_sessions (never the full history — that response grew unbounded
// with a bot's message count and took prod down for large bots) and lazily
// loads one session's messages from /api/chat_sessions/messages when the
// user opens it. Server owns date/platform/search filtering; this component
// owns the fetch/cache state and the filter chips.
const ChatHistories = ({ bots = [], selectedBotIds = [], addBot, startDate, endDate, setStartDate, setEndDate }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [totalSessions, setTotalSessions] = useState(0);
  const [platformCounts, setPlatformCounts] = useState({});
  const [loadingMore, setLoadingMore] = useState(false);
  const [messagesBySession, setMessagesBySession] = useState({});
  const [loadingMessages, setLoadingMessages] = useState({});
  const [messageErrors, setMessageErrors] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  // All filter chips are active by default. Each chip is independent;
  // toggling one only affects its own visibility.
  const platformFilters = useMemo(() => getPlatformFilters(), []);
  const [selectedPlatforms, setSelectedPlatforms] = useState(() => new Set(platformFilters.map((f) => f.key)));
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce the search box so we hit the server once per pause, not per key.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const togglePlatform = (key) => {
    setSelectedPlatforms((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Track if we've initialized dates from data
  const datesInitializedRef = useRef(false);
  // Set when we are about to write the server-provided date range into the
  // parent's date state: that state change re-creates fetchSessions, but the
  // no-date fetch that just completed already covered the full range, so the
  // next effect run must be a no-op. This is what previously caused the Inbox
  // to download everything TWICE per visit.
  const skipNextFetchRef = useRef(false);
  // AbortController for cancelling in-flight requests on re-render
  const abortControllerRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const messagesBySessionRef = useRef(messagesBySession);
  messagesBySessionRef.current = messagesBySession;
  const loadingMessagesRef = useRef(loadingMessages);
  loadingMessagesRef.current = loadingMessages;

  const botsKey = selectedBotIds.join(',');
  const platformsKey = useMemo(
    () => [...selectedPlatforms].sort().join(','),
    [selectedPlatforms]
  );
  const allPlatformsActive = selectedPlatforms.size === platformFilters.length;

  const buildSessionsUrl = useCallback((offset) => {
    let url = `${process.env.REACT_APP_API_BASE_URL}chat_sessions?bot_ids=${botsKey}&limit=${SESSIONS_PAGE_SIZE}&offset=${offset}`;
    if (startDate) url += `&start_date=${startDate}`;
    if (endDate) url += `&end_date=${endDate}`;
    if (!allPlatformsActive) url += `&platforms=${encodeURIComponent(platformsKey)}`;
    if (debouncedSearch) url += `&search=${encodeURIComponent(debouncedSearch)}`;
    return url;
  }, [botsKey, startDate, endDate, allPlatformsActive, platformsKey, debouncedSearch]);

  const fetchSessions = useCallback(async () => {
    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    if (!selectedBotIds.length || selectedPlatforms.size === 0) {
      setSessions([]);
      setTotalSessions(0);
      if (!selectedBotIds.length) setPlatformCounts({});
      setLoading(false);
      return;
    }

    setLoadError(false);
    try {
      const response = await fetch(buildSessionsUrl(0), { signal: abortController.signal });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      if (abortController.signal.aborted) return;
      const data = await response.json();

      setSessions(data.sessions || []);
      setTotalSessions(data.total_sessions || 0);
      setPlatformCounts(data.platform_counts || {});

      // Initialize the date picker from the bots' full range (only once).
      // The server sends date_range only on a no-date fetch, which by
      // definition already covered everything — so skip the refetch the
      // date-state change would otherwise trigger.
      if (!datesInitializedRef.current && data.date_range && setStartDate && setEndDate) {
        const { min_date, max_date } = data.date_range;
        if (min_date && max_date) {
          const today = dayjs();
          const maxDay = dayjs(max_date);
          skipNextFetchRef.current = true;
          // Subtract 1 day from min date to ensure we fetch all
          setStartDate(dayjs(min_date).subtract(1, 'day').format('YYYY-MM-DD'));
          setEndDate((maxDay.isAfter(today) ? maxDay : today).format('YYYY-MM-DD'));
          datesInitializedRef.current = true;
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Error fetching chat sessions:', err);
      setLoadError(true);
    } finally {
      if (!abortController.signal.aborted) setLoading(false);
    }
  }, [buildSessionsUrl, selectedBotIds.length, selectedPlatforms.size, setStartDate, setEndDate]);

  useEffect(() => {
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }
    fetchSessions();
  }, [fetchSessions]);

  // Reset per-bot state when the bot selection changes
  useEffect(() => {
    datesInitializedRef.current = false;
    setMessagesBySession({});
    setLoadingMessages({});
    setMessageErrors({});
  }, [botsKey]);

  const hasMore = sessions.length < totalSessions;

  const loadMoreSessions = useCallback(async () => {
    if (loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const response = await fetch(buildSessionsUrl(sessions.length));
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      setSessions((prev) => {
        const seen = new Set(prev.map((s) => s.session_id));
        return [...prev, ...(data.sessions || []).filter((s) => !seen.has(s.session_id))];
      });
      if (typeof data.total_sessions === 'number') setTotalSessions(data.total_sessions);
    } catch (err) {
      console.error('Error loading more chat sessions:', err);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [buildSessionsUrl, sessions.length]);

  // Lazy per-session message load; cached, so re-opening a thread is instant.
  const loadSessionMessages = useCallback(async (sessionId, { force = false } = {}) => {
    if (!sessionId) return;
    if (!force && (messagesBySessionRef.current[sessionId] || loadingMessagesRef.current[sessionId])) return;
    setLoadingMessages((prev) => ({ ...prev, [sessionId]: true }));
    setMessageErrors((prev) => ({ ...prev, [sessionId]: false }));
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}chat_sessions/messages?session_id=${encodeURIComponent(sessionId)}`
      );
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      setMessagesBySession((prev) => ({ ...prev, [sessionId]: Array.isArray(data) ? data : [] }));
    } catch (err) {
      console.error('Error loading session messages:', err);
      setMessageErrors((prev) => ({ ...prev, [sessionId]: true }));
    } finally {
      setLoadingMessages((prev) => ({ ...prev, [sessionId]: false }));
    }
  }, []);

  const botNameById = useMemo(() => {
    const map = {};
    (bots || []).forEach((b) => { map[b.id] = b.name; });
    return map;
  }, [bots]);

  // Shape the summaries for ChatHistoryOverview (legacy field names kept where
  // the overview already used them: id / date / dateKey / messages).
  const mappedSessions = useMemo(() => sessions.map((s) => ({
    id: s.session_id,
    botId: s.bot_id,
    botName: botNameById[s.bot_id] || '',
    platform: s.platform || 'website',
    channel: s.channel || s.platform || 'website', // the Inbox chip it belongs to ('portal' for test chats)
    country: s.country,
    preview: s.preview,
    messages: s.user_messages,
    date: formatSessionDate(s.last_activity),
    dateKey: s.last_activity,
    lastActivity: s.last_activity,
  })), [sessions, botNameById]);

  const handleDownloadChatHistory = async () => {
    if (!selectedBotIds.length) {
      alert(t('select_bot_to_download'));
      return;
    }
    try {
      for (const botId of selectedBotIds) {
        let url = `${process.env.REACT_APP_API_BASE_URL}chat_history_excel?bot_id=${botId}`;
        if (startDate) {
          url += `&start_date=${startDate}`;
        }
        if (endDate) {
          url += `&end_date=${endDate}`;
        }
        const response = await fetch(url, { method: 'GET' });
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const contentDisposition = response.headers.get('Content-Disposition');
        const filename = contentDisposition
          ? contentDisposition.split('filename=')[1]?.trim().replace(/"/g, '')
          : `chat_history_${botId}.xlsx`;

        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
      }
    } catch (err) {
      console.error('Error downloading chat history:', err);
      alert(t('error_downloading_chat_history'));
    }
  };

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);
  const handleConfirmDownload = async () => {
    setIsModalOpen(false);
    await handleDownloadChatHistory();
  };

  // Per-chip conversation count from the server aggregate — independent of
  // which chips are active and of the current search.
  const getPlatformCount = (platformKey) => platformCounts[platformKey] || 0;

  if (loading) {
    return (
      <div className="loading-container">
        <FontAwesomeIcon icon={faSpinner} spin className="main-loading-spinner" />
      </div>
    );
  }

  if ((bots?.length ?? 0) === 0) {
    return (
      <div className="bots-overview">
        <div className="bots-overview-header">
          <h1>⚠️ {t('no_bots_yet')}</h1>
        </div>
        <button className="create-here-button" onClick={addBot}>
          {t('create_here')}
        </button>
      </div>
    );
  }

  return (
    <div className="analytics-dashboard">
      <div>
        <div className='download-header-parent'>
          <div className="platform-filters">
            {platformFilters.map((filter) => {
              const count = getPlatformCount(filter.key);
              const isActive = selectedPlatforms.has(filter.key);
              const channelLabel = filter.labelKey ? t(filter.labelKey) : filter.label;
              return (
                <button
                  key={filter.key}
                  type="button"
                  className={`platform-filter ${isActive ? 'active' : ''}`}
                  onClick={() => togglePlatform(filter.key)}
                  aria-pressed={isActive}
                  title={isActive
                    ? t('chat_hist_hide_channel', { channel: channelLabel })
                    : t('chat_hist_show_channel', { channel: channelLabel })}
                >
                  <span className="platform-filter-count">{count}</span>
                  {filter.key === 'slack' ? (
                    /* Slack's brand mark is intentionally 4-color (red, blue,
                       green, yellow). The other channel chips keep the
                       monochrome FontAwesome glyph in their brand color. */
                    <SlackLogo size={14} style={{ marginRight: '6px' }} />
                  ) : (
                    <FontAwesomeIcon icon={filter.icon} style={{ color: filter.color, marginRight: '6px' }} />
                  )}
                  {channelLabel}
                  <PlatformFilterBorderDraw />
                </button>
              );
            })}
          </div>
          <button
            className="download-chat-history-btn"
            onClick={handleOpenModal}
            disabled={totalSessions === 0}
          >
            {t('export')}
            <FontAwesomeIcon className='download-chat-history-icon' icon={faDownload} />
          </button>
        </div>
        {loadError ? (
          <div className="no-chat-history-message inbox-load-error">
            <p>{t('inbox_load_failed')}</p>
            <button type="button" className="inbox-retry-button" onClick={fetchSessions}>
              <FontAwesomeIcon icon={faRotateRight} style={{ marginRight: '6px' }} />
              {t('inbox_try_again')}
            </button>
          </div>
        ) : mappedSessions.length === 0 && !debouncedSearch ? (
          <div className="no-chat-history-message">
            <p>
              {selectedPlatforms.size === 0
                ? t('chat_hist_select_channel')
                : (t('no_chat_history_message') || 'No chat history available for the selected channels.')}
            </p>
          </div>
        ) : (
          <ChatHistoryOverview
            sessions={mappedSessions}
            messagesBySession={messagesBySession}
            loadingMessages={loadingMessages}
            messageErrors={messageErrors}
            onSessionSelected={loadSessionMessages}
            onRetryMessages={(sessionId) => loadSessionMessages(sessionId, { force: true })}
            hasMore={hasMore}
            loadingMore={loadingMore}
            onLoadMore={loadMoreSessions}
            searchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
            selectedBotId={botsKey}
          />
        )}
      </div>

      <ConfirmDownloadModal
        open={isModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmDownload}
        startDate={startDate}
        endDate={endDate}
      />
    </div>
  );
};

export default ChatHistories;
